// Generated by GitHub Copilot
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { useComponentState } from '#core/state/State.ts'
import { NotificationPreferencesState } from '#components/domain/operations/settings/notifications/notification-preferences-state.ts'
import { NotificationSubscriptionsState } from '#components/domain/operations/settings/notifications/notification-subscriptions-state.ts'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import { AudienceSections } from '#components/domain/operations/settings/notifications/notification-audience-sections.tsx'
import { SubscriptionsList } from '#components/domain/operations/settings/notifications/notification-subscriptions-list.tsx'

export const Route = createFileRoute(
  '/_appSettings/$companyId/settings/notifications',
)({
  component: RouteComponent,
})

type NotificationsSettingsTab = 'globalPreferences' | 'subscriptions'

function RouteComponent() {
  return <NotificationSettingsPage />
}

/**
 * Notification settings page — a Global Preferences tab holding the company's subscription catalog
 * as an audience matrix, and a "Subscription Preferences" tab for the entities the user follows. Two
 * independent sources back them (catalog subscriptions vs follow rows), each in its own state; the
 * page combines their loading/error/save-error so the user sees a single surface.
 */
function NotificationSettingsPage() {
  const { t } = useTranslation('settings')
  const { t: tNav } = useTranslation('navigation')
  const notificationsBlocked = useSyncExternalStore(
    (onStoreChange) => {
      window.addEventListener('focus', onStoreChange)
      return () => { window.removeEventListener('focus', onStoreChange) }
    },
    () => typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'denied',
    () => false,
  )
  const preferences = useComponentState(NotificationPreferencesState)
  const subscriptions = useComponentState(NotificationSubscriptionsState)
  const [activeTab, setActiveTab] = useState<NotificationsSettingsTab>('globalPreferences')

  // Both states debounce lead-time writes; clear any pending timer when the page unmounts.
  // `useComponentState` returns a fresh proxy on every mutation (all wrapping one stable instance), so
  // we capture each in a ref and dispose only on unmount — depending on the states directly would cancel
  // the debounce timer (clearing `_hourTimers`) on the very re-render that `setHours` triggers, dropping
  // the pending PUT.
  const preferencesRef = useRef(preferences)
  const subscriptionsRef = useRef(subscriptions)
  useEffect(() => () => {
    preferencesRef.current.dispose()
    subscriptionsRef.current.dispose()
  }, [])

  const loading = preferences.loading || subscriptions.loading
  const loadError = preferences.error || subscriptions.error
  const saveError = preferences.saveError || subscriptions.saveError

  if (loading) {
    return (
      <div className="p-4 d-flex align-items-center gap-2 text-secondary">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        {t('notifications.loading')}
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="p-4">
        <div className="alert alert-danger d-flex flex-wrap align-items-center gap-2" role="alert">
          <span className="me-auto">{t('notifications.loadError')}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => { preferences.reload(); subscriptions.reload() }}
          >
            {t('shared.actions.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="mb-4">
        <h1 className="h4 fw-bold mb-1">{t('shared.fields.notifications')}</h1>
        <p className="text-secondary mb-0">{t('notifications.description')}</p>
      </div>

      {notificationsBlocked && (
        <div className='alert alert-warning d-flex align-items-start gap-3 mb-4' role='alert'>
          <Bell className='flex-shrink-0 mt-1' width={20} height={20} />
          <div>
            <div className='fw-semibold'>{tNav('notifications.blockedTitle')}</div>
            <div>{tNav('notifications.blockedMessage')}</div>
          </div>
        </div>
      )}

      {(saveError || subscriptions.syncError) && (
        <div className="alert alert-warning alert-dismissible d-flex align-items-center" role="alert">
          {saveError ? t('notifications.saveError') : t('notifications.syncError')}
          <button
            type="button"
            className="btn-close"
            aria-label={t('shared.common.dismiss')}
            onClick={() => { preferences.clearSaveError(); subscriptions.clearSaveError() }}
          />
        </div>
      )}

      <Tabs
        tabs={[
          { key: 'globalPreferences', label: t('notifications.globalPreferences.title') },
          { key: 'subscriptions', label: t('notifications.subscriptions.title') },
        ]}
        activeKey={activeTab}
        onChange={key => setActiveTab(key as NotificationsSettingsTab)}
        variant="underline"
        size="sm"
        className="mb-4"
      />

      {activeTab === 'globalPreferences' && (
        <>
          <p className="text-secondary mb-3">{t('notifications.globalPreferences.hint')}</p>
          <AudienceSections state={preferences} />
        </>
      )}

      {activeTab === 'subscriptions' && (
        <>
          <p className="text-secondary mb-3">{t('notifications.subscriptions.hint')}</p>
          <SubscriptionsList state={subscriptions} />
        </>
      )}
    </div>
  )
}
